import React from 'react';
import PropTypes from 'prop-types';
import EditorControlButtons from './EditorControlButtons';
import RatingsEditor from './Ratings/RatingsEditor';
import ReviewTextEditor from './Review/ReviewTextEditor';
import ShareEditor from './Share/ShareEditor';
import Fade from 'treeline-react-core-components/Fade';
import { withIsMobile } from 'treeline-react-core-components/WindowSizeProvider';
import { Result, Button, message, Tooltip } from 'antd';
import { useUserReviewEditor } from './data/UserReviewEditorProvider';
import NominationsEditor from './Share/NominationsEditor';
import useSaveEditor from './data/useSaveEditor';
import useSocialNetworkShareUris from './data/useSocialNetworkShareUris';
import './reviewEditor.css';
import LoadingSpinnerInContainer from '../../UI/LoadingSpinnerInContainer';

UserReviewEditor.propTypes = {
	isMobile: PropTypes.func,
	product: PropTypes.object,
	drawer: PropTypes.shape({ open: PropTypes.func, close: PropTypes.func }),
};

class UserReviewEditorUnmount extends React.Component {
	static propTypes = {
		children: PropTypes.element,
		editorController: PropTypes.object,
	};

	render() {
		return this.props.children;
	}

	componentWillUnmount() {
		this.props.editorController.reset();
	}
}

function UserReviewEditor({ drawer, product, isMobile }) {
	const {
		isLoading,
		reviewEditor,
		ratingsEditor,
		editorController,
		shareTargetsEditor,
	} = useUserReviewEditor();

	const [isNominating, setIsNominating] = React.useState(false);

	const { isLoading: isLoadingSave, handleSave } = useSaveEditor(
		editorController,
	);

	const { twitter, facebook } = useSocialNetworkShareUris(product.sku);

	const renderSocialNetworkShareButton = (icon, socialNetwork) => {
		const renderButton = () => (
			<Button
				icon={icon}
				shape="circle"
				target="_blank"
				disabled={socialNetwork.error.exists}
				loading={socialNetwork.isLoading}
				href={socialNetwork.uri}
			/>
		);

		if (isMobile()) {
			return renderButton();
		}

		return (
			<Tooltip
				title={`Share on ${socialNetwork.displayName}`}
				placement="bottomRight">
				{renderButton()}
			</Tooltip>
		);
	};

	const renderNominateButton = () => {
		return (
			<div style={{ display: 'flex', justifyContent: 'center' }}>
				<div style={{ width: 300 }}>
					<Button block onClick={() => setIsNominating(true)}>
						Nominate
					</Button>
					<div
						style={{
							display: 'flex',
							justifyContent: 'center',
							marginTop: '1rem',
						}}>
						<div
							style={{
								display: 'flex',
								width: 100,
								justifyContent: 'space-between',
							}}>
							{renderSocialNetworkShareButton(
								'facebook',
								facebook,
							)}
							{renderSocialNetworkShareButton('twitter', twitter)}
						</div>
					</div>
				</div>
			</div>
		);
	};

	const renderSaveWasCompleted = () => {
		return (
			<Fade>
				<Result
					status="success"
					title="Review Saved"
					subTitle="Consider sharing your review on the platforms below"
					extra={renderNominateButton()}
				/>
			</Fade>
		);
	};

	const handleNominationSave = async () => {
		await handleSave();
		setIsNominating(false);
		message.success(`Nominations saved for ${product.name}`);
	};

	const renderEditors = () => {
		if (isNominating) {
			return (
				<>
					<NominationsEditor />
					<div
						style={{
							marginTop: '1rem',
							display: 'flex',
							justifyContent: 'center',
						}}>
						<Button
							disabled={isLoadingSave}
							onClick={() => setIsNominating(false)}
							style={{ minWidth: 80 }}>
							Cancel
						</Button>
						<Button
							loading={isLoadingSave}
							style={{ minWidth: 80, marginLeft: '1rem' }}
							onClick={handleNominationSave}>
							Save
						</Button>
					</div>
				</>
			);
		}

		if (editorController.didSave()) {
			return renderSaveWasCompleted();
		}

		return (
			<div className="editors">
				<RatingsEditor ratingsDataEditor={ratingsEditor} />
				<ReviewTextEditor reviewEditor={reviewEditor} />
				<ShareEditor shareTargetsEditor={shareTargetsEditor} />
				<EditorControlButtons
					drawer={drawer}
					productName={product.name}
					editorController={editorController}
				/>
			</div>
		);
	};

	return (
		<UserReviewEditorUnmount editorController={editorController}>
			<div className="user-review-editor">
				{isLoading() ? (
					<LoadingSpinnerInContainer height="200px" />
				) : (
					renderEditors()
				)}
			</div>
		</UserReviewEditorUnmount>
	);
}

export default withIsMobile(UserReviewEditor);
